/**
 * useProjectAirsheds -- fetch & cache InMAP airshed polygons for a project.
 *
 * Polygons are keyed by project_id; they don't change for a given project
 * (derived from the displaced-plant list). Cache generously.
 *
 * When the backend polygon cache isn't warmed yet the endpoint returns an
 * empty FeatureCollection — we treat that as a non-error and fall back to
 * circular buffers in `RegionalAirshedLayer`.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchProjectAirsheds } from "../../../api/projects";
import { useAuth } from "../../../auth/AuthProvider";
import type { AirshedFeatureCollection } from "../../model/domain/types";

export function useProjectAirsheds(projectId: string | undefined, enabled = true) {
  const { token } = useAuth();
  return useQuery<AirshedFeatureCollection>({
    queryKey: ["airsheds", projectId],
    queryFn: () => fetchProjectAirsheds(projectId!, token!),
    enabled: Boolean(projectId) && Boolean(token) && enabled,
    staleTime: 60 * 60_000,   // 1 hour — polygons are static for a project
    gcTime: 2 * 60 * 60_000,  // 2 hours
    retry: 1,
  });
}
